import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleMap, MarkerF, PolylineF } from "@react-google-maps/api";
import { Box, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import api from "../../redux/apis/api";
import { getResolvedLiveSnapshot, resolveStatus } from "../../utils/vehicleStatus";

const mapContainerStyle = {
  width: "100%",
  height: "100vh",
};

const defaultCenter = { lat: 23.5937, lng: 78.9629 };

const statusLabelMap = {
  running: "Running",
  idle: "Idling",
  stopped: "Stopped",
  inactive: "No Reporting",
  expired: "Expired",
  no_data: "No Data",
};

const statusColorMap = {
  running: "#16a34a",
  idle: "#ca8a04",
  stopped: "#dc2626",
  inactive: "#0ea5e9",
  expired: "#f97316",
  no_data: "#6b7280",
};

const formatDateTime = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "NA";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export default function PublicLiveTrackingPage() {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("t") || "").trim();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trail, setTrail] = useState([]);

  const fetchTracking = useCallback(async () => {
    if (!token) {
      setError("Share link is missing.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/public/live-tracking", { params: { t: token } });
      const nextPayload = response?.data || null;
      setPayload(nextPayload);
      setError("");

      const snapshot = getResolvedLiveSnapshot(nextPayload?.device || {}, {});
      if (snapshot?.hasGps) {
        setTrail((prev) => {
          const point = { lat: snapshot.latitude, lng: snapshot.longitude };
          const lastPoint = prev[prev.length - 1];
          if (lastPoint && lastPoint.lat === point.lat && lastPoint.lng === point.lng) {
            return prev;
          }
          return [...prev.slice(-39), point];
        });
      }
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to load shared tracking.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setTrail([]);
    setPayload(null);
    setLoading(true);
    setError("");
    fetchTracking();
  }, [fetchTracking]);

  useEffect(() => {
    if (!token) return undefined;
    const timer = window.setInterval(fetchTracking, 15000);
    return () => window.clearInterval(timer);
  }, [fetchTracking, token]);

  const device = useMemo(() => payload?.device || {}, [payload]);
  const snapshot = useMemo(() => getResolvedLiveSnapshot(device, {}), [device]);
  const statusKey = String(resolveStatus(device, {})).toLowerCase();
  const markerPosition = snapshot?.hasGps
    ? { lat: snapshot.latitude, lng: snapshot.longitude }
    : null;
  const center = markerPosition || defaultCenter;

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f8fafc", p: 3 }}>
        <Paper elevation={3} sx={{ p: 3, maxWidth: 460, textAlign: "center" }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, mb: 1 }}>Shared Tracking</Typography>
          <Typography sx={{ color: "#dc2626", fontWeight: 600 }}>{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: "100vh", bgcolor: "#e5e7eb" }}>
      <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={markerPosition ? 15 : 5}>
        {trail.length > 1 ? (
          <PolylineF
            path={trail}
            options={{
              strokeColor: "#2563eb",
              strokeOpacity: 0.9,
              strokeWeight: 4,
            }}
          />
        ) : null}
        {markerPosition ? <MarkerF position={markerPosition} /> : null}
      </GoogleMap>

      <Paper
        elevation={4}
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 3,
          p: 2,
          minWidth: 280,
          maxWidth: 360,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.96)",
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
          {device?.name || device?.vehicleNumber || device?.imei || "Shared Vehicle"}
        </Typography>
        <Typography sx={{ color: "#4b5563", fontWeight: 600, mb: 1 }}>
          {device?.vehicleNumber || device?.imei || "NA"}
        </Typography>
        <Chip
          size="small"
          label={statusLabelMap[statusKey] || "No Data"}
          sx={{
            bgcolor: statusColorMap[statusKey] || "#6b7280",
            color: "#fff",
            fontWeight: 700,
            mb: 1.5,
          }}
        />
        <Typography sx={{ fontSize: 14, color: "#111827", fontWeight: 600 }}>
          Speed: {Number(snapshot?.speed || 0).toFixed(0)} km/h
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#4b5563", mt: 0.5 }}>
          Last update: {formatDateTime(device?.lastDataReceivedAt || device?.updatedAt)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#4b5563", mt: 0.5 }}>
          Link valid till: {formatDateTime(payload?.expiresAt)}
        </Typography>
        {markerPosition ? (
          <Typography sx={{ fontSize: 13, color: "#4b5563", mt: 0.5 }}>
            {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#9ca3af", mt: 0.5 }}>
            Live GPS location not available yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
